import React, {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import CreditCardForm from "components/CreditCardForm/CreditCardForm";
import {
  createBillingAddressValidationShape,
  createCreditCardValidationShape,
  getRestorableCreditCardFormValues,
  mergeCreditCardFormValues,
} from "components/CreditCardForm/creditCardFormHelper";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { useFormik } from "formik";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import { CreditCardFormValues } from "types/creditCard";
import * as yup from "yup";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { usePayPalAwareUserPaymentMethods } from "../../../../branding/usePayPalAvailability";
import { useUsdtDepositsEnabled } from "../../../../branding/useUsdtDeposits";
import {
  PaymentButtonSlot,
  PaymentFooter,
} from "../../../../components/PaymentButton";
import PayPalOrderButton, {
  PayPalOrderSkippedError,
} from "../../../../components/PayPalOrderButton/PayPalOrderButton";
import ServiceLogo from "../../../../components/ServiceLogo/ServiceLogo";
import StripePaymentForm from "../../../../components/StripePaymentForm";
import { getProfile } from "../../../../reduxFolder/reducers/auth";
import { getOutgoing } from "../../../../reduxFolder/reducers/outgoing";
import {
  clearPurchase,
  confirmationAll,
} from "../../../../reduxFolder/reducers/purchase";
import {
  closeRightPanel,
  openRightPanel,
} from "../../../../reduxFolder/reducers/rightPanel";
import {
  authCreditCardFeePercent,
  authOverpayFeePercent,
  authPaypalFeePercent,
  authSelectUnits,
  authSelectUser,
  authStripeFeePercent,
} from "../../../../reduxFolder/selectors/globalSelectors";
import {
  outgoingSelectPackingData,
  outgoingSelectPaymentData,
} from "../../../../reduxFolder/selectors/outgoingSelectors";
import { purchaseAllIsLoading } from "../../../../reduxFolder/selectors/purchaseSelectors";
import { Currency } from "../../../../styles/parcelsStyles";
import api from "../../../../utils/api";
import formatter from "../../../../utils/formatter";
import { toFiniteNumber } from "../../../../utils/number";
import {
  calculatePaymentFee,
  getPaymentFeePercent,
} from "../../../../utils/paymentFees";
import {
  isPaymentOption,
  toPaymentMethod,
  USDT_PAYMENT_OPTION,
  type PaymentMethod,
  type PaymentOption,
} from "../../../../utils/paymentMethods";
import { extractPayPalOrderId } from "../../../../utils/paypal";
import { finalizeStripePaymentForContext } from "../../../../utils/stripePaymentAdapter";
import { refreshOutgoingPaymentData } from "../../paymentRefresh";
import PaymentMethodSelector from "../PaymentMethodSelector";
import UsdtDepositCheckout from "../UsdtDepositCheckout/UsdtDepositCheckout";
import {
  Parcel,
  ParcelAmount,
  ParcelBreakdownRow,
  ParcelService,
  ParcelText,
  ParcelTitle,
  PaymentSection,
  PaymentSummary,
  PaymentSummaryAmount,
  PaymentSummaryRow,
  PaymentSummaryTotalRow,
  ScrollableContent,
  Spinner,
} from "./PayAllPanel.styles";

interface PayAllPanelProps {
  panelData?: any;
  isOpen: boolean;
  data: any;
  packingData: any;
  units: string;
  isLoadingAllPurchase?: boolean;
  user: any;
  authCreditCardFeePercent: string;
  authOverpayFeePercent: string;
  authPaypalFeePercent: string;
  authStripeFeePercent: string;
  openRightPanel: OpenRightPanel;
  closeRightPanel: () => void;
  clearPurchase: () => void;
  getOutgoing: (data?: any) => Promise<void>;
  getProfile: () => Promise<unknown>;
  confirmationAll: (data?: { paymentMethod?: PaymentMethod }) => void;
}

const PayAllPanel: FC<PayAllPanelProps> = React.memo(
  ({
    data,
    packingData,
    units,
    confirmationAll,
    isLoadingAllPurchase,
    panelData,
    isOpen,
    user,
    authCreditCardFeePercent,
    authOverpayFeePercent,
    authPaypalFeePercent,
    authStripeFeePercent,
    openRightPanel,
    closeRightPanel,
    clearPurchase,
    getOutgoing,
    getProfile,
  }) => {
    const { t, i18n } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const currentPanelData = panelData || Map();
    const items = data.get("items");
    const isLoading = data.get("isLoading");
    const totalPrice = data.get("totalPrice");
    const paypalSignIdRef = useRef<string | null>(null);
    const stripeSignIdRef = useRef<string | null>(null);
    const paymentMethodDefaults = {
      creditCardAllowedForUser: false,
      overpayAllowedForUser: false,
    };
    const {
      available: paymentMethods,
      isAvailable,
      paypalConfig,
      isPayPalAvailable,
    } = usePayPalAwareUserPaymentMethods(user, paymentMethodDefaults);
    // Invoiced users pay from balance (BE allows it to go negative), so the
    // payment-method choice does not apply to them.
    const isInvoiced = user.get("invoiced", false);
    const totalPriceValue = toFiniteNumber(totalPrice);
    const balanceValue = toFiniteNumber(user.get("balance", 0));
    const amountToPayBeforeFee = isInvoiced
      ? totalPriceValue
      : formatter.roundTo2Decimal(Math.max(totalPriceValue - balanceValue, 0));
    const needsExternalPaymentMethod = !isInvoiced && amountToPayBeforeFee > 0;
    // The selector renders for every non-invoiced user; USDT only makes sense
    // when something actually has to be paid on top of the balance.
    const usdtEnabled = useUsdtDepositsEnabled();
    const paymentOptions: PaymentOption[] =
      needsExternalPaymentMethod && usdtEnabled
        ? [...paymentMethods, USDT_PAYMENT_OPTION]
        : paymentMethods;
    const restoredPaymentMethod = currentPanelData.get("selectedPaymentMethod");
    const initialSelectedPaymentMethod = isPaymentOption(restoredPaymentMethod)
      ? restoredPaymentMethod
      : undefined;
    // paymentOptions (not paymentMethods) is the single source of truth for
    // the selection, otherwise the reset below would bounce a USDT choice.
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
      PaymentOption | undefined
    >(
      initialSelectedPaymentMethod &&
        paymentOptions.includes(initialSelectedPaymentMethod)
        ? initialSelectedPaymentMethod
        : paymentOptions[0],
    );
    useEffect(() => {
      if (
        selectedPaymentMethod &&
        paymentOptions.includes(selectedPaymentMethod)
      ) {
        return;
      }

      const nextPaymentMethod = paymentOptions[0];
      if (selectedPaymentMethod !== nextPaymentMethod) {
        setSelectedPaymentMethod(nextPaymentMethod);
      }
    }, [paymentOptions, selectedPaymentMethod]);
    const paymentFeePercent = getPaymentFeePercent(
      toPaymentMethod(selectedPaymentMethod),
      {
        creditCard: authCreditCardFeePercent,
        overpay: authOverpayFeePercent,
        paypal: authPaypalFeePercent,
        stripe: authStripeFeePercent,
      },
    );
    const paymentFee = needsExternalPaymentMethod
      ? calculatePaymentFee(amountToPayBeforeFee, paymentFeePercent)
      : 0;
    const totalToPay = formatter.roundTo2Decimal(
      amountToPayBeforeFee + paymentFee,
    );
    const paymentButtonTotal = needsExternalPaymentMethod
      ? totalToPay
      : totalPriceValue;
    const shouldUseExternalPayPal =
      needsExternalPaymentMethod && selectedPaymentMethod === "paypal";
    const shouldUseExternalStripe =
      needsExternalPaymentMethod && selectedPaymentMethod === "stripe";
    const shouldUseUsdt =
      needsExternalPaymentMethod &&
      selectedPaymentMethod === USDT_PAYMENT_OPTION;
    const initialCreditCardValues = mergeCreditCardFormValues(
      i18n.language,
      user.get("country", "US"),
      currentPanelData.get("creditCardFormValues"),
    );
    const creditCardFormik = useFormik<CreditCardFormValues>({
      validateOnBlur: false,
      initialValues: initialCreditCardValues,
      validationSchema: yup.object({
        ...createCreditCardValidationShape(t),
        billingAddress: yup.object(createBillingAddressValidationShape(t)),
      }),
      onSubmit: () => undefined,
    });

    const handlePayAll = useCallback(() => {
      const paymentMethod = toPaymentMethod(selectedPaymentMethod);
      confirmationAll(paymentMethod ? { paymentMethod } : {});
    }, [confirmationAll, selectedPaymentMethod]);

    const refreshPayAllData = useCallback(() => {
      void refreshOutgoingPaymentData({
        getOutgoing,
        paymentItemsCount: data.get("items").size,
        packingItemsCount: packingData.get("items").size,
      });
      closeRightPanel();
      clearPurchase();
    }, [clearPurchase, closeRightPanel, data, getOutgoing, packingData]);

    const confirmPurchase = useCallback(
      () => api.purchase_all.confirmation(),
      [],
    );

    // Balance may have changed while the panel was open. Confirmation either
    // finishes payment immediately or returns the sign_id needed externally.
    const prepareExternalPayment = useCallback(async () => {
      try {
        await api.purchase_all.confirmation();
        refreshPayAllData();
        return null;
      } catch (error: any) {
        const signId = error.data?.sign_id;
        if (!signId) throw error;

        return signId as string;
      }
    }, [refreshPayAllData]);

    const createPayPalOrder = useCallback(async () => {
      const signId = await prepareExternalPayment();
      if (!signId) throw new PayPalOrderSkippedError();

      paypalSignIdRef.current = signId;
      const response = await api.purchase_all.create_paypal_order({
        sign_id: signId,
      });
      return extractPayPalOrderId(response);
    }, [prepareExternalPayment]);

    const capturePayPalOrder = useCallback(
      async (orderId: string) => {
        const signId = paypalSignIdRef.current;

        if (!signId) {
          throw new Error("PayPal sign id is missing");
        }

        await api.purchase_all.capture_paypal_order({
          sign_id: signId,
          order_id: orderId,
        });
        await api.purchase_all.confirmation();
        paypalSignIdRef.current = null;
        refreshPayAllData();
      },
      [refreshPayAllData],
    );

    const createStripePaymentIntent = useCallback(async () => {
      const signId = await prepareExternalPayment();
      if (!signId) return null;

      stripeSignIdRef.current = signId;
      return api.purchase_all.create_stripe_payment_intent({ sign_id: signId });
    }, [prepareExternalPayment]);

    const completeStripePaymentIntent = useCallback(
      async (paymentIntentId: string) => {
        const signId = stripeSignIdRef.current;
        if (!signId) throw new Error("Stripe sign id is missing");

        await finalizeStripePaymentForContext(
          { flow: "purchase_all", signId },
          paymentIntentId,
        );
        stripeSignIdRef.current = null;
        refreshPayAllData();
      },
      [refreshPayAllData],
    );

    const switchPaymentMethod = useCallback((method: PaymentOption) => {
      setSelectedPaymentMethod(method);
    }, []);

    const helpPanelContext = {
      openRightPanel,
      returnPanelType: "PAY_ALL",
      getReturnPanelData: () =>
        currentPanelData
          .set("selectedPaymentMethod", selectedPaymentMethod)
          .set(
            "creditCardFormValues",
            getRestorableCreditCardFormValues(creditCardFormik.values),
          ),
    };

    const renderLoading = () => {
      return <Spinner isActive={isLoading} />;
    };

    const renderParcelsList = () => {
      return items.map((item: any, index: number) => {
        const deliveryName = item.get("preferred_carrier");
        const weight =
          item.get("total_weight") &&
          Number(item.get("total_weight")).toFixed(2);
        const deliveryCost = toFiniteNumber(
          item.getIn(["estimate", "price"], 0),
        );
        const insuranceCost = toFiniteNumber(
          item.getIn(["estimate", "insurance"], 0),
        );
        const shouldShowInsurance =
          insuranceCost > 0 ||
          item.getIn(["estimate", "require_insurance"], false);

        return (
          <Parcel key={item.get("id")}>
            <ParcelTitle>
              {`${index + 1} ${item.get("sku")}`}
              <span>
                <Currency>{currencySymbol}</Currency> {item.getIn(["estimate", "total"], 0)}
              </span>
            </ParcelTitle>
            <ParcelText>
              <ParcelService>
                <ServiceLogo serviceKey={deliveryName} />
                {`${deliveryName}, ${weight} ${t(`units.${units}.primaryShort`)}`}
              </ParcelService>
              <ParcelAmount>
                <Currency>{currencySymbol}</Currency> {formatter.currency(deliveryCost)}
              </ParcelAmount>
            </ParcelText>
            {shouldShowInsurance && (
              <ParcelBreakdownRow>
                {t("parcels.insurance")}
                <ParcelAmount>
                  <Currency>{currencySymbol}</Currency> {formatter.currency(insuranceCost)}
                </ParcelAmount>
              </ParcelBreakdownRow>
            )}
          </Parcel>
        );
      });
    };

    const renderPaymentSummary = () => {
      return (
        <PaymentSummary>
          <PaymentSummaryRow>
            {t("parcels.totalLabel")}
            <PaymentSummaryAmount>
              <Currency>{currencySymbol}</Currency> {formatter.currency(totalPriceValue)}
            </PaymentSummaryAmount>
          </PaymentSummaryRow>
          {balanceValue !== 0 && !isInvoiced && (
            <PaymentSummaryRow>
              {t("parcels.balance")}
              <PaymentSummaryAmount>
                <Currency>{currencySymbol}</Currency> {formatter.currency(balanceValue)}
              </PaymentSummaryAmount>
            </PaymentSummaryRow>
          )}
          {paymentFee > 0 && (
            <PaymentSummaryRow>
              {t("parcels.commissionFeeLabel", {
                fee: formatter.roundTo2Decimal(paymentFeePercent * 100),
              })}
              <PaymentSummaryAmount>
                <Currency>{currencySymbol}</Currency> {formatter.currency(paymentFee)}
              </PaymentSummaryAmount>
            </PaymentSummaryRow>
          )}
          <PaymentSummaryTotalRow>
            {t("parcels.totalToPay")}
            <PaymentSummaryAmount>
              <Currency>{currencySymbol}</Currency> {formatter.currency(paymentButtonTotal)}
            </PaymentSummaryAmount>
          </PaymentSummaryTotalRow>
        </PaymentSummary>
      );
    };

    return (
      <ContentWrapper>
        <Heading $isBordered>{t("common.payAll")}</Heading>
        <ScrollableContent>
          {isLoading ? (
            renderLoading()
          ) : (
            <>
              {renderParcelsList()}
              {renderPaymentSummary()}
              {!isInvoiced && (
                <PaymentSection>
                  <PaymentMethodSelector
                    paymentMethods={paymentOptions}
                    selectedPaymentMethod={selectedPaymentMethod}
                    onSelect={switchPaymentMethod}
                  />
                  {selectedPaymentMethod === "credit_card" &&
                    isAvailable("credit_card") && (
                      <CreditCardForm
                        formik={creditCardFormik}
                        cardSectionHeader={null}
                        fieldsTopSpacing="section"
                      />
                    )}
                </PaymentSection>
              )}
              {shouldUseExternalPayPal ? (
                <PayPalOrderButton
                  paypalConfig={paypalConfig}
                  createOrder={createPayPalOrder}
                  captureOrder={capturePayPalOrder}
                  fallbackChildren={`${t("common.payAll")} (${currencySymbol}${formatter.currency(paymentButtonTotal)})`}
                  helpPanelContext={helpPanelContext}
                  disabled={!isPayPalAvailable}
                />
              ) : shouldUseExternalStripe ? (
                <StripePaymentForm
                  createPaymentIntent={createStripePaymentIntent}
                  completePaymentIntent={completeStripePaymentIntent}
                  returnUrl={`${window.location.origin}/parcels/outgoing/all/stripe-return`}
                  fallbackChildren={`${t("common.payAll")} (${currencySymbol}${formatter.currency(paymentButtonTotal)})`}
                  helpPanelContext={helpPanelContext}
                />
              ) : shouldUseUsdt ? (
                <UsdtDepositCheckout
                  targetAmount={amountToPayBeforeFee}
                  sessionKey="purchase_all"
                  isOpen={isOpen}
                  helpPanelContext={helpPanelContext}
                  confirmPurchase={confirmPurchase}
                  refreshProfile={getProfile}
                  onPaid={refreshPayAllData}
                />
              ) : (
                <PaymentButtonSlot>
                  <PaymentFooter
                    disabled={
                      isLoadingAllPurchase ||
                      (needsExternalPaymentMethod && !selectedPaymentMethod) ||
                      (!isInvoiced && selectedPaymentMethod === "credit_card")
                    }
                    isLoading={isLoadingAllPurchase}
                    onClick={handlePayAll}
                    helpPanelContext={helpPanelContext}
                  >
                    {selectedPaymentMethod || !needsExternalPaymentMethod
                      ? `${t("common.payAll")} (${currencySymbol}${formatter.currency(paymentButtonTotal)})`
                      : t("common.noPaymentMethodsAvailable")}
                  </PaymentFooter>
                </PaymentButtonSlot>
              )}
            </>
          )}
        </ScrollableContent>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    data: outgoingSelectPaymentData,
    packingData: outgoingSelectPackingData,
    units: authSelectUnits,
    user: authSelectUser,
    authCreditCardFeePercent,
    authOverpayFeePercent,
    authPaypalFeePercent,
    authStripeFeePercent,
    isLoadingAllPurchase: purchaseAllIsLoading,
  }),
  {
    confirmationAll,
    clearPurchase,
    closeRightPanel,
    getOutgoing,
    getProfile,
    openRightPanel,
  },
);

export default withConnect(PayAllPanel);
